import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * When provided, the authenticated user's role must be in this list to view
   * the route. Authorized roles that don't match are redirected to /courses
   * (a safe authenticated landing) rather than shown the route's UI shell.
   */
  roles?: string[];
}

/**
 * Guards authenticated routes.
 *
 * - While the app is restoring the Cognito session on first load (isInitializing),
 *   renders nothing to avoid a flash-redirect to /login.
 * - Once initialisation settles: redirects to /login if not authenticated,
 *   carrying the interrupted path, or renders children if authenticated.
 * - When `roles` is provided, also enforces a client-side role check
 *   (defense-in-depth — the backend remains the source of truth).
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, roles }) => {
  const isInitializing = useSelector((state: RootState) => state.auth.isInitializing);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const location = useLocation();

  if (isInitializing) return null;

  // Remember where they were going, so signing in resumes it (PRT-BR-P01-03).
  // Login decides whether the target is safe to honour.
  if (!isAuthenticated) {
    return (
      <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
    );
  }

  if (roles && roles.length > 0 && !roles.includes(role ?? '')) {
    return <Navigate to="/courses" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
